import {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  supabase,
} from "../supabaseClient";

import logo from "../assets/logo_basic.png";


export default function PublicSiteHeader({
  active = "",
}) {
  const navigate =
    useNavigate();


  // =====================================================
  // SESSION
  // =====================================================

  const [
    session,
    setSession,
  ] =
    useState(null);


  const [
    displayName,
    setDisplayName,
  ] =
    useState("");


  // =====================================================
  // MOBILE MENU
  // =====================================================

  const [
    mobileOpen,
    setMobileOpen,
  ] =
    useState(false);


  // =====================================================
  // SESSION LOAD
  // =====================================================

  useEffect(
    () => {
      let mounted =
        true;


      const applySession =
        async (
          currentSession
        ) => {
          if (!mounted) {
            return;
          }


          setSession(
            currentSession ||
            null
          );


          if (
            !currentSession
              ?.user
          ) {
            setDisplayName("");

            return;
          }


          const authUser =
            currentSession.user;


          try {
            const {
              data: dbUser,
              error:
                dbUserError,
            } =
              await supabase
                .from(
                  "users"
                )
                .select(
                  "name"
                )
                .eq(
                  "auth_uid",
                  authUser.id
                )
                .maybeSingle();


            if (
              dbUserError
            ) {
              console.warn(
                "[PUBLIC HEADER] User lookup warning:",
                dbUserError
                  .message
              );
            }


            const fullName =
              dbUser?.name ||
              authUser
                ?.user_metadata
                ?.full_name ||
              authUser
                ?.user_metadata
                ?.name ||
              authUser
                ?.user_metadata
                ?.given_name ||
              authUser
                ?.email
                ?.split(
                  "@"
                )?.[0] ||
              "Member";


            const firstName =
              fullName
                .toString()
                .trim()
                .split(
                  /\s+/
                )[0] ||
              "Member";


            const formattedName =
              firstName
                .charAt(0)
                .toUpperCase() +
              firstName
                .slice(1)
                .toLowerCase();


            if (
              mounted
            ) {
              setDisplayName(
                formattedName
              );
            }
          } catch (
            error
          ) {
            console.error(
              "[PUBLIC HEADER] User lookup failed:",
              error
            );
          }
        };


      const loadSession =
        async () => {
          try {
            const {
              data,
            } =
              await supabase
                .auth
                .getSession();


            await applySession(
              data?.session ||
              null
            );
          } catch (
            error
          ) {
            console.error(
              "[PUBLIC HEADER] Session load failed:",
              error
            );
          }
        };


      loadSession();


      const {
        data:
          authListener,
      } =
        supabase.auth
          .onAuthStateChange(
            async (
              _event,
              newSession
            ) => {
              await applySession(
                newSession
              );
            }
          );


      return () => {
        mounted =
          false;


        authListener
          ?.subscription
          ?.unsubscribe();
      };
    },
    []
  );


  // =====================================================
  // CLOSE MOBILE MENU
  // =====================================================

  const closeMobile =
    () => {
      setMobileOpen(
        false
      );
    };


  // =====================================================
  // NAVIGATE + CLOSE MOBILE MENU
  // =====================================================

  const goTo =
    (
      path
    ) => {
      closeMobile();

      navigate(
        path
      );
    };


  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout =
    async () => {
      try {
        closeMobile();


        await supabase.auth
          .signOut();


        navigate(
          "/landing",
          {
            replace:
              true,
          }
        );
      } catch (
        error
      ) {
        console.error(
          "[PUBLIC HEADER] Logout failed:",
          error
        );
      }
    };


  // =====================================================
  // REGISTER / GET MY AUDIT
  //
  // Visitor chooses plan first.
  // =====================================================

  const goToPricing =
    () => {
      closeMobile();


      navigate({
        pathname:
          "/landing",

        hash:
          "#pricing",
      });
    };


  // =====================================================
  // DESKTOP NAV CLASS
  // =====================================================

  const navClass =
    (
      key
    ) =>
      `px-2 py-2 transition whitespace-nowrap ${
        active === key
          ? "text-[#EB6AB5] font-semibold"
          : "text-white/80 hover:text-white"
      }`;


  // =====================================================
  // MOBILE NAV CLASS
  // =====================================================

  const mobileNavClass =
    (
      key
    ) =>
      `w-full text-left px-4 py-3 rounded-xl transition ${
        active === key
          ? "bg-[#EB6AB5]/10 text-[#EB6AB5] font-semibold"
          : "text-white/80 hover:text-white hover:bg-white/5"
      }`;


  return (
    <header
      className="
        sticky
        top-0
        z-50
        w-full
        border-b
        border-[#EB6AB5]/20
        bg-[#11111f]/95
        backdrop-blur-lg
      "
    >

      {/* =====================================================
          MAIN HEADER ROW
      ===================================================== */}

      <div
        className="
          max-w-7xl
          mx-auto
          px-4
          sm:px-6
          lg:px-8
          h-[72px]
          flex
          items-center
          justify-between
          gap-4
        "
      >

        {/* =====================================================
            LOGO
        ===================================================== */}

        <button
          type="button"
          onClick={() =>
            goTo(
              "/landing"
            )
          }
          className="
            flex
            items-center
            gap-3
            flex-shrink-0
          "
          aria-label="Go to Reignite Spirit home page"
        >

          <img
            src={logo}
            alt="Reignite Spirit"
            className="
              w-11
              h-11
              object-contain
              drop-shadow-[0_0_12px_#eb6fbd]
            "
          />


          {/* Hide brand text on very small screens */}

          <div
            className="
              text-left
              hidden
              sm:block
            "
          >
            <div
              className="
                text-[#EB6AB5]
                text-lg
                font-bold
                tracking-wide
              "
            >
              REIGNITE.SPIRIT
            </div>

            <div
              className="
                text-[#EB6AB5]/70
                text-xs
                tracking-[0.16em]
              "
            >
              REKINDLE PURPOSE
            </div>
          </div>
        </button>


        {/* =====================================================
            DESKTOP NAVIGATION
        ===================================================== */}

        <nav
          className="
            hidden
            lg:flex
            items-center
            gap-2
            xl:gap-3
            text-sm
          "
        >

          {/* HOME */}

          <button
            type="button"
            onClick={() =>
              goTo(
                "/landing"
              )
            }
            className={
              navClass(
                "home"
              )
            }
          >
            Home
          </button>


          {/* FOUNDING TRUTH */}

          <button
            type="button"
            onClick={() =>
              goTo(
                "/foundation-truth"
              )
            }
            className={
              navClass(
                "foundation"
              )
            }
          >
            Our Founding Truth
          </button>


          {/* BLOG */}

          <button
            type="button"
            onClick={() =>
              goTo(
                "/blog"
              )
            }
            className={
              navClass(
                "blog"
              )
            }
          >
            Blog
          </button>


          {/* FAQ */}

          <button
            type="button"
            onClick={() =>
              goTo(
                "/faq"
              )
            }
            className={
              navClass(
                "faq"
              )
            }
          >
            FAQ
          </button>


          {/* =====================================================
              LOGGED-IN USER
          ===================================================== */}

          {session ? (
            <>
              <span
                className="
                  px-2
                  py-2
                  text-[#EB6AB5]
                  font-semibold
                  whitespace-nowrap
                "
              >
                Hi{" "}
                {displayName} 👋
              </span>


              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/dashboard"
                  )
                }
                className="
                  px-3
                  py-2
                  rounded-lg
                  border
                  border-[#EB6AB5]/40
                  text-white
                  hover:bg-[#EB6AB5]/10
                  transition
                  whitespace-nowrap
                "
              >
                Dashboard
              </button>


              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/questionnaire"
                  )
                }
                className="
                  px-3
                  py-2
                  rounded-lg
                  bg-[#cb01bd]
                  hover:bg-[#e94ce0]
                  text-white
                  font-bold
                  transition
                  whitespace-nowrap
                "
              >
                My Audit
              </button>


              <button
                type="button"
                onClick={
                  handleLogout
                }
                className="
                  px-3
                  py-2
                  rounded-lg
                  text-white/70
                  hover:text-white
                  hover:bg-white/10
                  transition
                  whitespace-nowrap
                "
              >
                Logout
              </button>
            </>
          ) : (
            <>

              {/* LOGIN */}

              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/login"
                  )
                }
                className="
                  px-3
                  py-2
                  rounded-lg
                  border
                  border-white/20
                  text-white
                  hover:bg-white/10
                  transition
                "
              >
                Login
              </button>


              {/* REGISTER */}

              <button
                type="button"
                onClick={
                  goToPricing
                }
                className="
                  px-3
                  py-2
                  rounded-lg
                  border
                  border-white/20
                  text-white
                  hover:bg-white/10
                  transition
                  whitespace-nowrap
                "
              >
                Register
              </button>


              {/* GET MY AUDIT */}

              <button
                type="button"
                onClick={
                  goToPricing
                }
                className="
                  px-3
                  py-2
                  rounded-lg
                  bg-[#cb01bd]
                  hover:bg-[#e94ce0]
                  text-white
                  font-bold
                  transition
                  whitespace-nowrap
                "
              >
                Get My Audit
              </button>
            </>
          )}
        </nav>


        {/* =====================================================
            MOBILE / TABLET HAMBURGER
        ===================================================== */}

        <button
          type="button"
          onClick={() =>
            setMobileOpen(
              (
                current
              ) =>
                !current
            )
          }
          className="
            lg:hidden
            w-11
            h-11
            rounded-xl
            border
            border-[#EB6AB5]/40
            flex
            items-center
            justify-center
            text-white
            hover:bg-[#EB6AB5]/10
            transition
            flex-shrink-0
          "
          aria-label="Toggle navigation menu"
          aria-expanded={
            mobileOpen
          }
        >

          {mobileOpen ? (
            <span
              className="
                text-3xl
                font-light
                leading-none
              "
            >
              ×
            </span>
          ) : (
            <div
              className="
                flex
                flex-col
                gap-[5px]
              "
            >
              <span
                className="
                  block
                  w-6
                  h-[2px]
                  bg-white
                  rounded-full
                "
              />

              <span
                className="
                  block
                  w-6
                  h-[2px]
                  bg-white
                  rounded-full
                "
              />

              <span
                className="
                  block
                  w-6
                  h-[2px]
                  bg-white
                  rounded-full
                "
              />
            </div>
          )}
        </button>
      </div>


      {/* =====================================================
          MOBILE NAVIGATION
      ===================================================== */}

      {mobileOpen && (
        <div
          className="
            lg:hidden
            border-t
            border-[#EB6AB5]/20
            bg-[#11111f]
            shadow-2xl
          "
        >
          <div
            className="
              max-w-7xl
              mx-auto
              px-4
              sm:px-6
              py-4
            "
          >

            <nav
              className="
                flex
                flex-col
                gap-1
              "
            >

              {/* HOME */}

              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/landing"
                  )
                }
                className={
                  mobileNavClass(
                    "home"
                  )
                }
              >
                Home
              </button>


              {/* FOUNDING TRUTH */}

              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/foundation-truth"
                  )
                }
                className={
                  mobileNavClass(
                    "foundation"
                  )
                }
              >
                Our Founding Truth
              </button>


              {/* BLOG */}

              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/blog"
                  )
                }
                className={
                  mobileNavClass(
                    "blog"
                  )
                }
              >
                Blog
              </button>


              {/* FAQ */}

              <button
                type="button"
                onClick={() =>
                  goTo(
                    "/faq"
                  )
                }
                className={
                  mobileNavClass(
                    "faq"
                  )
                }
              >
                FAQ
              </button>


              {/* =================================================
                  LOGGED-IN MOBILE MENU
              ================================================= */}

              {session ? (
                <>

                  <div
                    className="
                      mt-3
                      mb-1
                      px-4
                      py-3
                      rounded-xl
                      bg-[#EB6AB5]/10
                      text-[#EB6AB5]
                      font-semibold
                    "
                  >
                    Hi{" "}
                    {displayName} 👋
                  </div>


                  <button
                    type="button"
                    onClick={() =>
                      goTo(
                        "/dashboard"
                      )
                    }
                    className="
                      mt-2
                      w-full
                      px-4
                      py-3
                      rounded-xl
                      border
                      border-[#EB6AB5]/40
                      text-white
                      hover:bg-[#EB6AB5]/10
                      transition
                      text-center
                    "
                  >
                    Dashboard
                  </button>


                  <button
                    type="button"
                    onClick={() =>
                      goTo(
                        "/questionnaire"
                      )
                    }
                    className="
                      mt-2
                      w-full
                      px-4
                      py-3
                      rounded-xl
                      bg-[#cb01bd]
                      hover:bg-[#e94ce0]
                      text-white
                      font-bold
                      transition
                    "
                  >
                    My Audit
                  </button>


                  <button
                    type="button"
                    onClick={
                      handleLogout
                    }
                    className="
                      mt-2
                      w-full
                      px-4
                      py-3
                      rounded-xl
                      border
                      border-white/10
                      text-white/70
                      hover:text-white
                      hover:bg-white/10
                      transition
                    "
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>

                  {/* =============================================
                      LOGIN / REGISTER
                  ============================================= */}

                  <div
                    className="
                      grid
                      grid-cols-2
                      gap-3
                      mt-4
                    "
                  >

                    <button
                      type="button"
                      onClick={() =>
                        goTo(
                          "/login"
                        )
                      }
                      className="
                        px-3
                        py-3
                        rounded-xl
                        border
                        border-white/20
                        text-white
                        hover:bg-white/10
                        transition
                      "
                    >
                      Login
                    </button>


                    <button
                      type="button"
                      onClick={
                        goToPricing
                      }
                      className="
                        px-3
                        py-3
                        rounded-xl
                        border
                        border-white/20
                        text-white
                        hover:bg-white/10
                        transition
                      "
                    >
                      Register
                    </button>

                  </div>


                  {/* GET MY AUDIT */}

                  <button
                    type="button"
                    onClick={
                      goToPricing
                    }
                    className="
                      mt-3
                      w-full
                      px-4
                      py-3.5
                      rounded-xl
                      bg-[#cb01bd]
                      hover:bg-[#e94ce0]
                      text-white
                      font-bold
                      transition
                    "
                  >
                    Get My Audit
                  </button>

                </>
              )}

            </nav>
          </div>
        </div>
      )}

    </header>
  );
}